import React, { forwardRef, useState, useEffect } from 'react'
import { normalizeNumberString, currencyAsText, numberAsText } from '../utils/formatters'

type CurrencyInputProps = {
    field: any
    form: any
    allowNegative?: boolean
    allowDecimal?: boolean
    maxDecimals?: number
    onBlur?: () => void
    onEnterKey?: () => void
    onTabKey?: () => void
    onEscapeKey?: () => void
    onChange?: (value: number) => void
    [key: string]: any // for other HTML input props
}

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
    (
        {
            field,
            form,
            allowNegative = true,
            allowDecimal = true,
            maxDecimals = 2,
            onEnterKey,
            onTabKey,
            onEscapeKey,
            onBlur,
            onChange,
            ...props
        },
        ref
    ) => {
        const [value, setValue] = useState('')

        useEffect(() => {
            if (typeof field.value == 'string') {
                setValue(Number.isNaN(parseFloat(field.value)) ? '' : currencyAsText(field.value))
            } else if (field.value == null) {
                setValue('')
            } else {
                setValue(currencyAsText(field.value))
            }
        }, [field.value])

        function submitValue(fieldValue: string) {
            let finalValue: number | ''

            if (fieldValue) {
                const formattedNumber = normalizeNumberString(fieldValue)
                finalValue = Number.parseFloat(formattedNumber)
                setValue(currencyAsText(finalValue))
            } else if (field.value === 0) {
                finalValue = 0
                setValue(currencyAsText(0))
            } else {
                finalValue = ''
                setValue('')
            }

            if (onChange) onChange(finalValue || 0)
            form.setFieldValue(field.name, finalValue)
        }

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            setValue(e.target.value)
        }

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            // Allow navigation and editing keys
            const allowedKeys = [
                'Backspace',
                'Delete',
                'Tab',
                'Escape',
                'Enter',
                'ArrowLeft',
                'ArrowRight',
                'ArrowUp',
                'ArrowDown',
                'Home',
                'End',
            ]

            if (allowedKeys.includes(e.key)) {
                if (e.key === 'Enter') {
                    submitValue(e.currentTarget.value)
                    if (onEnterKey) {
                        e.preventDefault()
                        onEnterKey()
                        return
                    }
                }
                if (e.key === 'Tab' && onTabKey) {
                    onTabKey()
                    return
                }

                if (e.key === 'Escape' && onEscapeKey) {
                    onEscapeKey()
                    return
                }

                return // Allow the key
            }

            // Allow Ctrl/Cmd combinations (copy, paste, select all, etc.)
            if (e.ctrlKey || e.metaKey) {
                return
            }

            // Allow digits
            if (/\d/.test(e.key)) {
                return
            }

            // Allow decimal point if enabled
            if (allowDecimal && e.key === ',') {
                // Don't allow if there's already a decimal point
                const currentValue = (e.target as HTMLInputElement).value
                if (currentValue.includes(',')) {
                    e.preventDefault()
                }
                return
            }

            // Allow decimal point if enabled
            if (allowDecimal && e.key === '.') {
                // Don't allow if there's already a decimal point
                const currentValue = (e.target as HTMLInputElement).value
                if (currentValue.includes('.')) {
                    e.preventDefault()
                }
                return
            }

            // Allow minus sign if enabled and it's at the beginning
            if (allowNegative && e.key === '-') {
                const input = e.target as HTMLInputElement
                const cursorPosition = input.selectionStart || 0

                // Only allow minus at the beginning
                if (cursorPosition === 0 && !input.value.startsWith('-')) {
                    return
                }
            }

            // Block all other keys
            e.preventDefault()
        }

        const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
            e.preventDefault()
            const input = e.target as HTMLInputElement
            const pastedText = e.clipboardData.getData('text')

            // Get current cursor position
            const start = input.selectionStart || 0
            const end = input.selectionEnd || 0

            // Get current value and insert pasted text at cursor position
            const currentValue = input.value
            const newValue = currentValue.slice(0, start) + pastedText + currentValue.slice(end)

            let finalValue: number | ''

            if (newValue) {
                const formattedNumber = normalizeNumberString(newValue)
                finalValue = Number.parseFloat(formattedNumber)
                setValue(currencyAsText(finalValue))
            } else if (field.value === 0) {
                finalValue = 0
                setValue(currencyAsText(0))
            } else {
                finalValue = ''
                setValue('')
            }

            form.setFieldValue(field.name, finalValue)
        }

        return (
            <input
                ref={ref}
                {...props}
                name={field.name}
                type="text"
                inputMode="decimal"
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onBlur={e => {
                    submitValue(e.target.value)
                    if (onBlur) onBlur()
                }}
                onFocus={() => {
                    setValue(field.value === 0 || typeof field.value == 'string' ? '' : numberAsText(field.value))
                }}
                value={value}
                autoComplete="off"
            />
        )
    }
)
